/* ============================================================================
   LEITOR DE RECEITA — indicacao de lente monofocal (Claude 20.0, interface v2)

   O cliente envia a foto da receita (ou digita os valores), confere o que foi
   lido, responde duas perguntas sobre o uso e recebe a indicacao de espessura
   e tratamento. "Aplicar" abre o configurador e marca as opcoes sozinho — o
   cliente cai na etapa seguinte com tudo escolhido e pode trocar o que quiser.

   ARQUITETURA
     LahRx (motor)  funcoes puras: le os numeros, classifica o grau, escolhe
                    espessura e tratamento. Nao toca no DOM — o mesmo motor
                    serve a pagina de produto, a pagina de receitas e testes.
     Interface      JavaScript puro, sem Alpine: o script carrega depois do
                    Alpine e nao depende da ordem dos scripts.

   A FOTO
     Vai para o Worker da Cloudflare (endereco em Configuracoes do tema >
     Leitor de receita). O Worker guarda a chave da IA e devolve os valores;
     nada e armazenado. Endereco "demo" simula uma leitura para testes.

   PRIVACIDADE
     Nenhum valor de grau vai para o dataLayer: os eventos levam apenas
     etapa, origem e a indicacao final.

   LIGACAO COM O CONFIGURADOR
     Cada opcao do configurador recebeu data-item-handle (o handle do
     metaobjeto). A aplicacao clica nas opcoes pelo handle, na ordem do
     fluxo — o configurador registra eventos e calcula preco como se fosse o
     cliente clicando.
   ============================================================================ */

(function (global) {
    'use strict';

    /* ------------------------------------------------------------------
       MOTOR
       ------------------------------------------------------------------ */

    /* Limites do laboratorio, os mesmos publicados nas opcoes de espessura.
       "esf" tambem limita o meridiano mais forte (ESF + CIL): e a parte da
       lente que define a borda. Confirmar com o laboratorio se o limite e
       por ESF e CIL separados ou pela soma — aqui usamos a soma, que e o
       criterio mais conservador. */
    var MATERIAIS = [
        { handle: 'normal', nome: 'Normal', esf: 4, cil: 2 },
        { handle: 'fina', nome: 'Fina', esf: 6, cil: 6 },
        { handle: 'superfina', nome: 'Super Fina', esf: 10, cil: 6, fator: 0.5, pct: '50%' }
    ];
    MATERIAIS[0].fator = 1;
    MATERIAIS[1].fator = 0.7;
    MATERIAIS[1].pct = '30%';

    /* Multifocal: faixas publicadas em cada opcao ("ideal para graus ate
       +/- 2,75 / 4,75 / 10,00"). O limite de cilindro nao esta publicado:
       usamos o mesmo da monofocal (Normal ate -2,00) — confirmar com o
       laboratorio. */
    var MATERIAIS_MULTI = [
        { handle: 'multifocal-normal', nome: 'Normal', esf: 2.75, cil: 2, fator: 1 },
        { handle: 'multifocal-fina', nome: 'Fina', esf: 4.75, cil: 6, fator: 0.7, pct: '30%' },
        { handle: 'multifocal-super-fina', nome: 'Super Fina', esf: 10, cil: 6, fator: 0.5 }
    ];

    /* Campo de visao da multifocal, pela rotina do cliente. */
    var CAMPOS = {
        parado: {
            handle: 'base', nome: 'Base', linha: 'Nitidez e visão equilibrada',
            porque: 'Sua rotina é mais parada, com leitura, celular e TV: o campo Base entrega nitidez com o melhor custo.'
        },
        alterna: {
            handle: 'amplo', nome: 'Amplo', linha: 'Campo maior e adaptação mais rápida',
            porque: 'Você alterna o dia todo entre perto, computador e longe: o Amplo tem campo maior e adaptação mais rápida.'
        },
        movimento: {
            handle: 'dinamico', nome: 'Dinâmico', linha: 'Máximo campo e transição mais suave',
            porque: 'Você passa boa parte do dia em movimento: o Dinâmico tem a transição mais suave entre as distâncias e o máximo de campo.'
        }
    };

    var TELAS_TXT = { pouco: 'menos de 2 horas', medio: 'de 2 a 6 horas', muito: 'mais de 6 horas' };

    var TRATAMENTOS = {
        classico: { handle: 'normal2', nome: 'Clássico', linha: 'Antirreflexo, antirrisco e UV400' },
        azul: { handle: 'filtro-de-luz-azul', nome: 'Filtro de luz azul', linha: 'Filtra a luz das telas' },
        foto: { handle: 'fotossensivel', nome: 'Fotossensível', linha: 'Escurece no sol e clareia em ambientes internos' },
        combo: { handle: 'filtro-de-luz-azul-fotossensivel', nome: 'Filtro de luz azul + Fotossensível', linha: 'Filtra a luz das telas e escurece no sol' }
    };

    var LIMITE = { esf: 10, cil: 6 };

    /* "−2,25", "-2.25", "+1", "plano", "PL", "0" -> numero ou null */
    function num(v) {
        if (v === null || v === undefined) return null;
        var s = String(v).trim().toLowerCase();
        if (s === '') return null;
        if (/^(pl|plano|neutro|esf|0+([.,]0+)?)$/.test(s)) return 0;
        s = s.replace(/−|–|—/g, '-').replace(',', '.').replace(/[^0-9.+-]/g, '');
        if (s === '' || s === '+' || s === '-') return null;
        var n = parseFloat(s);
        if (isNaN(n)) return null;
        return Math.round(n * 4) / 4;
    }

    function eixoNum(v) {
        if (v === null || v === undefined || v === '') return null;
        var n = parseInt(String(v).replace(/[^0-9]/g, ''), 10);
        if (isNaN(n)) return null;
        if (n === 0) n = 180;
        if (n < 1 || n > 180) return null;
        return n;
    }

    /* Um olho no formato de cilindro negativo, o mesmo do configurador.
       Receitas de optometrista as vezes vem com CIL positivo: a transposicao
       (ESF + CIL, CIL com sinal trocado, eixo girado 90 graus) descreve a
       mesma lente. */
    function olho(o) {
        o = o || {};
        var esf = num(o.esf), cil = num(o.cil), eixo = eixoNum(o.eixo);
        if (esf === null && cil === null) return { vazio: true, esf: 0, cil: 0, eixo: null, transposto: false };
        esf = esf || 0;
        cil = cil || 0;
        var transposto = false;
        if (cil > 0) {
            esf = esf + cil;
            cil = -cil;
            if (eixo !== null) eixo = eixo <= 90 ? eixo + 90 : eixo - 90;
            transposto = true;
        }
        return { vazio: false, esf: esf, cil: cil, eixo: eixo, transposto: transposto };
    }

    function forte(o) {
        return Math.max(Math.abs(o.esf), Math.abs(o.esf + o.cil));
    }

    /* Meridiano mais forte com sinal (ex.: -3,25), para os textos. */
    function forteAssinado(o) {
        var a = o.esf, b = o.esf + o.cil;
        return Math.abs(b) > Math.abs(a) ? b : a;
    }

    /* "Seu grau chega a -3,75" confunde quem le -2,75 na receita quando o
       valor vem da soma com o cilindro: nesse caso, dizemos isso. */
    function textoGrau(olhos, sig) {
        var maxEsf = Math.max.apply(null, olhos.map(function (o) { return Math.abs(o.esf); }));
        return (Math.abs(sig) > maxEsf ? 'Somando esférico e cilindro, seu grau chega a ' : 'Seu grau chega a ') + fmt(sig);
    }

    function maiorAssinado(olhos) {
        var m = 0;
        olhos.forEach(function (o) { var v = forteAssinado(o); if (Math.abs(v) > Math.abs(m)) m = v; });
        return m;
    }

    function semGrau(o) { return o.vazio || (o.esf === 0 && o.cil === 0); }

    function banda(v, a, b) { return v <= a ? 0 : (v <= b ? 1 : 2); }

    /* Faixas usuais: miopia ate 3 / 6, hipermetropia ate 2 / 5,
       astigmatismo ate 1 / 2. */
    function classifica(o) {
        var partes = [];
        if (o.esf < 0) partes.push({ tipo: 'miopia', valor: o.esf, nivel: banda(-o.esf, 3, 6) });
        if (o.esf > 0) partes.push({ tipo: 'hipermetropia', valor: o.esf, nivel: banda(o.esf, 2, 5) });
        if (o.cil < 0) partes.push({ tipo: 'astigmatismo', valor: o.cil, nivel: banda(-o.cil, 1, 2) });
        return partes;
    }

    var NIVEL_F = ['leve', 'moderada', 'alta'];
    var NIVEL_M = ['leve', 'moderado', 'alto'];

    function fmt(v) {
        if (v === null || v === undefined) return '—';
        if (v === 0) return '0,00';
        return (v > 0 ? '+' : '−') + Math.abs(v).toFixed(2).replace('.', ',');
    }

    function textoParte(p) {
        var nivel = p.tipo === 'astigmatismo' ? NIVEL_M[p.nivel] : NIVEL_F[p.nivel];
        return p.tipo + ' ' + nivel + ' (' + fmt(p.valor) + ')';
    }

    function textoOlho(o) {
        if (semGrau(o)) return 'sem grau';
        var partes = classifica(o);
        var base = partes.filter(function (p) { return p.tipo !== 'astigmatismo'; });
        var ast = partes.filter(function (p) { return p.tipo === 'astigmatismo'; });
        var t = base.map(textoParte).join('');
        if (ast.length) t = t ? t + ' com ' + textoParte(ast[0]) : textoParte(ast[0]);
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    function titulo(od, oe) {
        var olhos = { od: od, oe: oe };
        var base = [], comBase = [], comAst = [];
        ['od', 'oe'].forEach(function (k) {
            classifica(olhos[k]).forEach(function (p) {
                if (p.tipo === 'astigmatismo') { if (comAst.indexOf(k) === -1) comAst.push(k); return; }
                if (base.indexOf(p.tipo) === -1) base.push(p.tipo);
                if (comBase.indexOf(k) === -1) comBase.push(k);
            });
        });
        if (!base.length && !comAst.length) return 'Sem grau';
        function onde(l, curto) {
            if (l.length === 2) return ' nos dois olhos';
            var lado = l[0] === 'od' ? 'direito' : 'esquerdo';
            return curto ? ' no ' + lado : ' no olho ' + lado;
        }
        var t;
        if (!base.length) t = 'astigmatismo' + onde(comAst);
        else if (!comAst.length) t = base.join(' e ') + onde(comBase);
        else if (comAst.length === comBase.length && comAst.every(function (k) { return comBase.indexOf(k) !== -1; })) {
            t = base.join(' e ') + ' com astigmatismo' + onde(comBase);
        } else {
            t = base.join(' e ') + onde(comBase) + ', com astigmatismo' + onde(comAst, true);
        }
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    /* Receita completa a partir da leitura (foto) ou do formulario.
       leitura: { longe:{od,oe}, perto:{od,oe}, adicao, prisma }
       Devolve { modo: 'longe'|'perto', od, oe, add, prisma } */
    function montaReceita(leitura) {
        leitura = leitura || {};
        var lg = leitura.longe || {}, pt = leitura.perto || {};
        var L = { od: olho(lg.od), oe: olho(lg.oe) };
        var P = { od: olho(pt.od), oe: olho(pt.oe) };
        var add = num(leitura.adicao);
        if (add !== null) add = Math.abs(add);
        if (add === 0) add = null;

        var temLonge = !L.od.vazio || !L.oe.vazio;
        var temPerto = !P.od.vazio || !P.oe.vazio;

        if (!temLonge && temPerto) {
            return { modo: 'perto', od: P.od, oe: P.oe, add: null, prisma: !!leitura.prisma };
        }
        /* Receita com as duas linhas e sem "adicao" escrita: a adicao e a
           diferenca entre perto e longe. */
        if (add === null && temLonge && temPerto) {
            var ref = !P.od.vazio && !L.od.vazio ? 'od' : 'oe';
            var d = Math.round((P[ref].esf - L[ref].esf) * 4) / 4;
            if (d > 0) add = d;
        }
        return { modo: 'longe', od: L.od, oe: L.oe, add: add, prisma: !!leitura.prisma };
    }

    /* Valores usados na indicacao conforme o uso escolhido. */
    function paraUso(rx, uso) {
        if (rx.modo === 'perto' || uso !== 'perto' || !rx.add) return { od: rx.od, oe: rx.oe };
        function soma(o) {
            return { vazio: false, esf: o.esf + rx.add, cil: o.cil, eixo: o.eixo, transposto: o.transposto };
        }
        return { od: soma(rx.od), oe: soma(rx.oe) };
    }

    function foraDaFaixa(par, prisma) {
        if (prisma) return 'prisma';
        var fora = [par.od, par.oe].some(function (o) {
            return Math.abs(o.esf) > LIMITE.esf || -o.cil > LIMITE.cil || forte(o) > LIMITE.esf;
        });
        return fora ? 'grau' : '';
    }

    function compativel(m, o) {
        return Math.abs(o.esf) <= m.esf && -o.cil <= m.cil && forte(o) <= m.esf;
    }

    /* cfg: { fina: 3.25, superfina: 6.25 } — a partir de qual grau (meridiano
       mais forte) indicar cada espessura. A compatibilidade com o laboratorio
       sempre vence: abaixo do limiar, se a Normal nao atende, sobe. */
    function espessura(par, cfg) {
        cfg = cfg || {};
        var limFina = typeof cfg.fina === 'number' ? cfg.fina : 3.25;
        var limSuper = typeof cfg.superfina === 'number' ? cfg.superfina : 6.25;
        var maior = Math.max(forte(par.od), forte(par.oe));
        var maiorCil = Math.max(-par.od.cil, -par.oe.cil);

        var min = -1;
        for (var i = 0; i < MATERIAIS.length; i++) {
            if (compativel(MATERIAIS[i], par.od) && compativel(MATERIAIS[i], par.oe)) { min = i; break; }
        }
        if (min === -1) return null;

        var pref = maior >= limSuper ? 2 : (maior >= limFina ? 1 : 0);
        var idx = Math.max(min, pref);
        var m = MATERIAIS[idx];
        var sig = maiorAssinado([par.od, par.oe]);
        var motivo, porque;

        if (idx === 0) {
            motivo = 'Para o seu grau, a Normal atende bem e é a opção mais leve no bolso.';
            porque = textoGrau([par.od, par.oe], sig) + ': a Normal atende bem e é a opção mais em conta.';
        } else if (idx === min && pref < min) {
            var ant = MATERIAIS[idx - 1];
            if (maiorCil > ant.cil) {
                motivo = 'Seu astigmatismo (' + fmt(-maiorCil) + ') passa do limite da ' + ant.nome + ' (até ' + fmt(-ant.cil) + '), então a ' + m.nome + ' é a indicada.';
                porque = 'Seu astigmatismo de ' + fmt(-maiorCil) + ' passa do limite da ' + ant.nome + ', por isso a ' + m.nome + '.';
            } else {
                motivo = 'Seu grau passa do limite da ' + ant.nome + ' (até ' + ant.esf.toFixed(2).replace('.', ',') + '), então a ' + m.nome + ' é a indicada.';
                porque = textoGrau([par.od, par.oe], sig) + ' e passa do limite da ' + ant.nome + ', por isso a ' + m.nome + '.';
            }
        } else {
            var lim = idx === 2 ? limSuper : limFina;
            motivo = 'A partir de ' + lim.toFixed(2).replace('.', ',') + ' de grau, a ' + m.nome + ' deixa a borda mais fina e os óculos mais leves.';
            if (min < idx) motivo += ' A ' + MATERIAIS[min].nome + ' também atende, se preferir.';
            porque = textoGrau([par.od, par.oe], sig) + ': a ' + m.nome + ' deixa a lente ' + m.pct + ' mais fina e os óculos mais leves.';
        }

        return {
            handle: m.handle,
            nome: m.nome,
            motivo: motivo,
            porque: porque,
            maior: maior,
            fator: m.fator,
            pct: m.pct || '',
            compativeis: MATERIAIS.slice(min).map(function (x) { return x.handle; })
        };
    }

    /* Espessura da multifocal. A lente tem a parte de longe e a de perto
       (longe + adicao): vale o meridiano mais forte entre as duas. */
    function espessuraMulti(par, add) {
        var olhos = [par.od, par.oe];
        if (add) {
            olhos = olhos.concat([par.od, par.oe].map(function (o) {
                return { vazio: false, esf: o.esf + add, cil: o.cil, eixo: o.eixo };
            }));
        }
        var min = -1;
        for (var i = 0; i < MATERIAIS_MULTI.length; i++) {
            var mat = MATERIAIS_MULTI[i];
            if (olhos.every(function (o) { return compativel(mat, o); })) { min = i; break; }
        }
        if (min === -1) return null;
        var m = MATERIAIS_MULTI[min];
        var sig = maiorAssinado(olhos);
        var maior = Math.abs(sig);
        var maiorCil = Math.max(-par.od.cil, -par.oe.cil);
        var porque;
        if (min === 0) {
            porque = textoGrau(olhos, sig) + ': a Normal atende bem e é a opção mais em conta.';
        } else if (maiorCil > MATERIAIS_MULTI[min - 1].cil) {
            porque = 'Seu astigmatismo de ' + fmt(-maiorCil) + ' passa do limite da ' + MATERIAIS_MULTI[min - 1].nome + ', por isso a ' + m.nome + '.';
        } else {
            porque = textoGrau(olhos, sig) + ', acima do indicado para a ' + MATERIAIS_MULTI[min - 1].nome + ': a ' + m.nome + ' deixa a lente mais fina e leve.';
        }
        return {
            handle: m.handle,
            nome: m.nome,
            motivo: porque,
            porque: porque,
            maior: maior,
            fator: m.fator,
            pct: m.pct || '',
            compativeis: MATERIAIS_MULTI.slice(min).map(function (x) { return x.handle; })
        };
    }

    /* rotina: 'parado' | 'alterna' | 'movimento' */
    function campo(rotina) {
        var c = CAMPOS[rotina] || CAMPOS.alterna;
        return { handle: c.handle, nome: c.nome, linha: c.linha, porque: c.porque };
    }

    /* telas: 'pouco' | 'medio' | 'muito'   sol: 'sim' | 'nao' */
    function tratamento(resp) {
        resp = resp || {};
        var azul = resp.telas === 'medio' || resp.telas === 'muito';
        var foto = resp.sol === 'sim';
        var horas = TELAS_TXT[resp.telas] || '';
        var t, motivo, porque;
        if (azul && foto) {
            t = TRATAMENTOS.combo;
            motivo = 'Você passa bastante tempo em telas e quer que a lente escureça no sol: este tratamento reúne os dois.';
            porque = 'Você passa ' + horas + ' por dia em telas e quer lente que escurece no sol: por isso o filtro azul e o fotossensível juntos.';
        } else if (azul) {
            t = TRATAMENTOS.azul;
            motivo = 'Com várias horas por dia em telas, o filtro azul reduz o cansaço visual.';
            porque = 'Você passa ' + horas + ' por dia em telas: o filtro azul reduz o cansaço visual.';
        } else if (foto) {
            t = TRATAMENTOS.foto;
            motivo = 'A lente escurece sozinha ao sol e volta a ficar clara em ambientes internos.';
            porque = 'Você quer lente que escurece no sol e usa pouco as telas: o fotossensível atende sem o custo do filtro azul.';
        } else {
            t = TRATAMENTOS.classico;
            motivo = 'Para pouco uso de telas, o Clássico já traz antirreflexo, antirrisco e proteção UV.';
            porque = 'Você usa pouco as telas e não quer lente que escurece: o Clássico já traz antirreflexo, antirrisco e UV400.';
        }
        return { handle: t.handle, nome: t.nome, linha: t.linha, motivo: motivo, porque: porque, fotossensivel: foto };
    }

    var LahRx = {
        num: num, olho: olho, forte: forte, classifica: classifica,
        textoOlho: textoOlho, titulo: titulo, fmt: fmt, semGrau: semGrau,
        montaReceita: montaReceita, paraUso: paraUso, foraDaFaixa: foraDaFaixa,
        espessura: espessura, tratamento: tratamento,
        espessuraMulti: espessuraMulti, campo: campo, forteAssinado: forteAssinado,
        MATERIAIS: MATERIAIS, MATERIAIS_MULTI: MATERIAIS_MULTI, TRATAMENTOS: TRATAMENTOS, CAMPOS: CAMPOS
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = LahRx;
    global.LahRx = LahRx;

    if (typeof document === 'undefined') return;

    /* ------------------------------------------------------------------
       INTERFACE (v2 — Claude 20.0)

       Cada tela devolve tres partes: visual (coluna esquerda no desktop),
       corpo (conteudo rolavel) e rodape (botoes fixos embaixo — no celular
       ficam ao alcance do polegar). A moldura e criada uma vez por abertura;
       trocar de tela troca so o conteudo.
       ------------------------------------------------------------------ */

    var STEP_TIPO = 'escolha-o-seu-tipo-de-lente';
    var STEP_ESP = 'selecione-a-espessura-de-suas-lentes';
    var STEP_TRAT = 'selecione-o-tratamento-de-suas-lentes';
    var STEP_CAMPO = 'escolha-o-campo-de-visao';
    var STEP_ESP_MULTI = 'selecione-a-espessura-de-suas-lentes-multifocais';

    /* Barra de progresso: em qual etapa cada tela esta. */
    var ETAPAS = ['Receita', 'Seu grau', 'Rotina', 'Indicação'];
    var ETAPA_DA_TELA = {
        inicio: 0, lendo: 0, erro: 0,
        conferir: 1, uso: 1, grau: 1,
        rotina: 2, telas: 2, sol: 2,
        resumo: 3, multifocal: 3, fora: 3, semgrau: 3
    };

    /* Frases exibidas durante a leitura. Fatos da Lahtref — editar aqui. */
    var FRASES = [
        'Lentes monofocais ficam prontas em 1 dia útil no nosso laboratório.',
        'Todas as lentes Lahtref têm antirreflexo, antirrisco e proteção UV400.',
        'A lente Fina é 30% mais fina que a Normal. A Super Fina, 50%.',
        'Frete grátis e até 6x sem juros em todo o site.',
        'Quase 5 mil clientes já avaliaram a Lahtref.'
    ];

    var PASSOS_LEITURA = [
        'Recebendo a foto',
        'Lendo os valores de cada olho',
        'Conferindo sinais, cilindro e eixo',
        'Separando as lentes compatíveis'
    ];

    var DEMO = {
        e_receita: true, legivel: true, confianca: 'alta', prisma: false,
        longe: { od: { esf: '-2.25', cil: '-0.75', eixo: '180' }, oe: { esf: '-2.75', cil: '-1.00', eixo: '175' } },
        perto: { od: { esf: '', cil: '', eixo: '' }, oe: { esf: '', cil: '', eixo: '' } },
        adicao: '', dp: '', observacao: ''
    };

    var REDUZIR = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    function esc(s) {
        return String(s === null || s === undefined ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function track(evento, dados) {
        try {
            if (typeof global.lahTrack === 'function') { global.lahTrack(evento, dados || {}); return; }
            global.dataLayer = global.dataLayer || [];
            global.dataLayer.push(Object.assign({ event: evento }, dados || {}));
        } catch (e) { /* analytics nunca quebra a venda */ }
    }

    function dinheiro(centavos) {
        return 'R$ ' + (centavos / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    /* Listas dos seletores — iguais as do configurador. */
    function faixa(min, max, passo) {
        var l = [];
        for (var v = max; v >= min - 1e-9; v -= passo) l.push(Math.round(v * 100) / 100);
        return l;
    }
    var ESF_OPC = faixa(-10, 10, 0.25);
    var CIL_OPC = faixa(-6, 6, 0.25);
    var ADD_OPC = faixa(0.75, 3.5, 0.25).reverse();

    function valorSelect(v) { return v === null || v === undefined ? '' : String(v); }

    function opcoes(lista, atual, rotuloVazio) {
        var a = atual === null || atual === undefined || atual === '' ? null : Number(atual);
        var tem = a === null || lista.some(function (v) { return Math.abs(v - a) < 1e-9; });
        var l = lista.slice();
        if (!tem) l.unshift(a); /* valor lido fora da lista continua visivel */
        var h = rotuloVazio !== undefined ? '<option value="">' + esc(rotuloVazio) + '</option>' : '';
        l.forEach(function (v) {
            var sel = a !== null && Math.abs(v - a) < 1e-9 ? ' selected' : '';
            h += '<option value="' + v + '"' + sel + '>' + esc(fmt(v)) + '</option>';
        });
        return h;
    }

    function opcoesEixo(atual) {
        var h = '<option value="">—</option>';
        for (var i = 1; i <= 180; i++) {
            h += '<option value="' + i + '"' + (Number(atual) === i ? ' selected' : '') + '>' + i + '°</option>';
        }
        return h;
    }

    function itemEl(wrap, step, handle) {
        if (!wrap) return null;
        return wrap.querySelector('.product-lens-select-step[data-step-selector="' + step + '"] .product-lens-select-item[data-item-handle="' + handle + '"]');
    }

    /* Preco de uma opcao do configurador, lido do proprio configurador. */
    function precoItem(wrap, step, handle) {
        var el = itemEl(wrap, step, handle);
        if (!el) return null;
        var inp = el.querySelector('[name="item-variant-price"]');
        return inp ? Number(inp.value) || 0 : 0;
    }

    /* ---------------- icones (traco fino, mesma familia do tema) ---------------- */

    function svg(d, tam) {
        tam = tam || 22;
        return '<svg viewBox="0 0 24 24" width="' + tam + '" height="' + tam + '" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + d + '</svg>';
    }
    var IC = {
        fechar: svg('<path d="M6 6l12 12M18 6L6 18"/>', 20),
        voltar: svg('<path d="M15 6l-6 6 6 6"/>', 20),
        foto: svg('<path d="M4 8h3l2-2.5h6L17 8h3v11H4z"/><circle cx="12" cy="13" r="3.5"/>'),
        lapis: svg('<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>', 20),
        cadeado: svg('<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>', 16),
        check: svg('<path d="M5 12.5l4.5 4.5L19 7.5"/>', 16),
        tela: svg('<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16v4"/>', 26),
        sol: svg('<circle cx="12" cy="12" r="4"/><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4"/>', 26),
        lente: svg('<circle cx="12" cy="12" r="8.5"/><path d="M8 9.5a5 5 0 0 1 4-2"/>', 26),
        ampliar: svg('<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5 5M10.5 8v5M8 10.5h5"/>', 18),
        rotina: svg('<circle cx="6" cy="18" r="2"/><circle cx="18" cy="6" r="2"/><path d="M8 18h6.5a3 3 0 0 0 0-6h-5a3 3 0 0 1 0-6H16"/>', 26),
        info: svg('<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.6v.1"/>', 18),
        dica: svg('<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z"/>', 18),
        whats: svg('<path d="M4 20l1.3-3.9A8 8 0 1 1 8 18.8z"/><path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1.2-1.4-2-1-1 .9a4 4 0 0 1-2.2-2.2l.9-1-1-2z"/>', 20)
    };

    /* Ilustracao da introducao: receita + lente. Traco claro sobre o verde. */
    var ILUSTRACAO =
        '<svg class="lah-rxl__ilustra" viewBox="0 0 320 300" aria-hidden="true">' +
        '<rect x="34" y="40" width="210" height="232" rx="10" fill="none" stroke="currentColor" stroke-opacity=".9" stroke-width="1.5"/>' +
        '<path d="M58 72h92M58 88h60" stroke="currentColor" stroke-opacity=".55" stroke-width="1.5" stroke-linecap="round"/>' +
        '<g font-family="Helvetica Neue, Helvetica, Arial, sans-serif" font-size="11" fill="currentColor">' +
        '<text x="58" y="128" fill-opacity=".6">ESF</text><text x="118" y="128" fill-opacity=".6">CIL</text><text x="176" y="128" fill-opacity=".6">EIXO</text>' +
        '<text x="34" y="152" dx="-2" text-anchor="end" fill-opacity=".6">OD</text>' +
        '<text x="58" y="152">−2,25</text><text x="118" y="152">−0,75</text><text x="176" y="152">180°</text>' +
        '<text x="34" y="178" dx="-2" text-anchor="end" fill-opacity=".6">OE</text>' +
        '<text x="58" y="178">−2,75</text><text x="118" y="178">−1,00</text><text x="176" y="178">175°</text>' +
        '</g>' +
        '<path d="M58 136h162M58 162h162M58 188h162" stroke="currentColor" stroke-opacity=".25"/>' +
        '<path d="M58 236c14-10 26 8 40-2s22-12 34 0" stroke="currentColor" stroke-opacity=".55" stroke-width="1.5" fill="none" stroke-linecap="round"/>' +
        '<circle cx="232" cy="168" r="62" fill="currentColor" fill-opacity=".08" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M196 150a40 40 0 0 1 26-28" stroke="currentColor" stroke-opacity=".7" stroke-width="1.5" fill="none" stroke-linecap="round"/>' +
        '</svg>';

    /* Perfil da lente: corte lateral, na mesma escala, Normal ao lado da
       indicada. Miopia = borda grossa; hipermetropia = centro grosso. */
    function perfilLente(maior, positivo, fator, nome) {
        var W = 120, base = 10, n = 28;
        var grosso = Math.min(40, 6 + maior * 4);
        var H = base + grosso + 4;
        function forma(esp, classe) {
            var topo = [], baixo = [];
            for (var i = 0; i <= n; i++) {
                var x = i / n, u = 2 * x - 1;
                var sag = 7 * (1 - u * u);
                var t = positivo ? 2.5 + (esp - 2.5) * (1 - u * u) : 2.5 + (esp - 2.5) * u * u;
                var px = (x * W).toFixed(1);
                topo.push(px + ',' + (base - sag).toFixed(1));
                baixo.unshift(px + ',' + (base - sag + t).toFixed(1));
            }
            return '<svg viewBox="-2 0 ' + (W + 4) + ' ' + H + '" aria-hidden="true"><polygon class="' + classe + '" points="' + topo.concat(baixo).join(' ') + '"/></svg>';
        }
        var h = '<div class="lah-rxl__perfis">';
        if (fator < 1) h += '<figure class="lah-rxl__perfil">' + forma(grosso, 'is-normal') + '<figcaption>Normal</figcaption></figure>';
        h += '<figure class="lah-rxl__perfil">' + forma(2.5 + (grosso - 2.5) * fator, 'is-indicada') + '<figcaption>' + esc(nome) + '</figcaption></figure>';
        return h + '</div>';
    }

    /* Medidor de 3 faixas (leve / moderado / alto) com marcador. */
    function medidor(p) {
        var cortes = p.tipo === 'miopia' ? [3, 6, 10] : (p.tipo === 'hipermetropia' ? [2, 5, 10] : [1, 2, 6]);
        var v = Math.abs(p.valor), pos;
        if (v <= cortes[0]) pos = v / cortes[0] / 3;
        else if (v <= cortes[1]) pos = (1 + (v - cortes[0]) / (cortes[1] - cortes[0])) / 3;
        else pos = (2 + Math.min(1, (v - cortes[1]) / (cortes[2] - cortes[1]))) / 3;
        pos = Math.max(0.02, Math.min(0.98, pos));
        var nomes = p.tipo === 'astigmatismo' ? ['leve', 'moderado', 'alto'] : ['leve', 'moderada', 'alta'];
        var rotulo = p.tipo.charAt(0).toUpperCase() + p.tipo.slice(1);
        return '<div class="lah-rxl__medidor">' +
            '<div class="lah-rxl__medidor-topo"><span>' + rotulo + '</span><strong>' + nomes[p.nivel] + ' <span class="lah-rxl__num">' + esc(fmt(p.valor)) + '</span></strong></div>' +
            '<div class="lah-rxl__trilho" role="img" aria-label="' + rotulo + ' ' + nomes[p.nivel] + '">' +
            '<span></span><span></span><span></span>' +
            '<i style="left:' + (pos * 100).toFixed(1) + '%"></i>' +
            '</div>' +
            '<div class="lah-rxl__trilho-legenda"><span>' + nomes[0] + '</span><span>' + nomes[1] + '</span><span>' + nomes[2] + '</span></div>' +
            '</div>';
    }

    /* ---------------- componente ---------------- */

    function Leitor(raiz) {
        this.raiz = raiz;
        this.wrap = raiz.closest('[x-data="ProductLensSelect"]');
        this.endpoint = (raiz.getAttribute('data-endpoint') || '').trim();
        this.whats = raiz.getAttribute('data-whats') || '';
        this.cfg = {
            fina: parseFloat(raiz.getAttribute('data-fina')) || 3.25,
            superfina: parseFloat(raiz.getAttribute('data-superfina')) || 6.25
        };
        this.cor = (getComputedStyle(raiz).getPropertyValue('--rxl-cor') || '').trim();
        this.modal = null;
        this.timers = [];
        this.reset();
    }

    Leitor.prototype.reset = function () {
        this.limparTimers();
        this.tela = 'inicio';
        this.hist = [];
        this.origem = '';
        this.arquivo = null;
        this.preview = '';
        this.leitura = null;
        this.form = {
            modo: 'longe',
            od: { esf: '', cil: '', eixo: '' },
            oe: { esf: '', cil: '', eixo: '' },
            add: ''
        };
        this.erros = {};
        this.erroLeitura = '';
        this.rx = null;
        this.uso = '';
        this.resp = { rotina: '', telas: '', sol: '' };
        this.multi = false;
        this.campo = null;
        this.esp = null;
        this.trat = null;
        this.fora = '';
        this.passo = 0;
        this.frase = 0;
        this.transposto = false;
        this.prisma = false;
    };

    Leitor.prototype.limparTimers = function () {
        (this.timers || []).forEach(function (t) { clearTimeout(t); clearInterval(t); });
        this.timers = [];
    };

    /* Armacao escolhida na pagina: nome, foto e preco da variante atual. */
    Leitor.prototype.armacao = function () {
        var info = { titulo: '', variante: '', imagem: '', preco: 0 };
        try {
            var j = JSON.parse(document.getElementById('product-lens-json-data').textContent);
            var btn = this.wrap && this.wrap.querySelector('.openProductLens');
            var m = btn && (btn.getAttribute('@click') || '').match(/openLens\('[^']*',\s*'([^']+)'/);
            var form = m && document.getElementById(m[1]);
            var inp = form && form.querySelector('[name="id"]');
            var id = inp ? Number(inp.value) : 0;
            var v = (j.variants || []).filter(function (x) { return x.id === id; })[0] || (j.variants || [])[0] || {};
            info.titulo = j.title || '';
            info.variante = v.title && v.title !== 'Default Title' ? v.title : '';
            var src = (v.featured_image && v.featured_image.src) || j.featured_image || '';
            if (src) info.imagem = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=900';
            info.preco = Number(v.price) || 0;
        } catch (e) { /* sem dados da armacao, as telas seguem sem foto e sem total */ }
        return info;
    };

    Leitor.prototype.abrir = function () {
        this.reset();
        this.arm = this.armacao();
        if (!this.modal) {
            var m = document.createElement('div');
            m.className = 'lah-rxl-modal';
            m.setAttribute('role', 'dialog');
            m.setAttribute('aria-modal', 'true');
            m.setAttribute('aria-label', 'Leitor de receita');
            /* Fica no body: dentro da coluna da PDP, conteineres com sticky e
               transform criariam outro contexto de empilhamento. */
            if (this.cor) m.style.setProperty('--rxl-cor', this.cor);
            document.body.appendChild(m);
            this.modal = m;
            var self = this;
            m.addEventListener('click', function (e) { self.clique(e); });
            m.addEventListener('change', function (e) { self.mudanca(e); });
            m.addEventListener('dragover', function (e) { self.arrastar(e, true); });
            m.addEventListener('dragleave', function (e) { self.arrastar(e, false); });
            m.addEventListener('drop', function (e) { self.soltar(e); });
            document.addEventListener('keydown', function (e) {
                if (e.key !== 'Escape' || !self.modal.classList.contains('is-open')) return;
                var zoom = self.modal.querySelector('.lah-rxl__zoom');
                if (zoom) { zoom.remove(); return; }
                self.fechar();
            });
        }
        this.modal.innerHTML = '';
        this.modal.classList.add('is-open');
        document.documentElement.classList.add('lah-rxl-travado');
        this.ultimoFoco = document.activeElement;
        this.render();
        track('rx_leitor_open', { endpoint: this.endpoint ? (this.endpoint === 'demo' ? 'demo' : 'worker') : 'nenhum' });
    };

    Leitor.prototype.fechar = function (aplicado) {
        if (!this.modal) return;
        this.limparTimers();
        if (!aplicado) track('rx_leitor_fechar', { tela: this.tela });
        this.modal.classList.remove('is-open');
        document.documentElement.classList.remove('lah-rxl-travado');
        if (this.preview) { try { URL.revokeObjectURL(this.preview); } catch (e) { } }
        if (!aplicado && this.ultimoFoco && this.ultimoFoco.focus) { try { this.ultimoFoco.focus(); } catch (e) { } }
    };

    Leitor.prototype.ir = function (tela, semHistorico) {
        this.limparTimers();
        if (!semHistorico && this.tela !== tela && this.tela !== 'lendo') this.hist.push(this.tela);
        this.tela = tela;
        this.render(true);
    };

    Leitor.prototype.voltar = function () {
        var t = this.hist.pop();
        if (t) { this.limparTimers(); this.tela = t; this.render(true); }
    };

    /* ---------------- eventos ---------------- */

    Leitor.prototype.clique = function (e) {
        if (e.target === this.modal) { this.fechar(); return; }
        var el = e.target.closest('[data-acao]');
        if (!el || !this.modal.contains(el)) return;
        var a = el.getAttribute('data-acao');
        var v = el.getAttribute('data-valor');

        switch (a) {
            case 'fechar': this.fechar(); break;
            case 'voltar': this.voltar(); break;
            case 'foto': {
                var inp = this.modal.querySelector('.lah-rxl__arquivo');
                if (inp) { inp.value = ''; inp.click(); }
                break;
            }
            case 'digitar':
                this.origem = 'manual';
                this.leitura = null;
                track('rx_leitor_manual', {});
                this.ir('conferir');
                break;
            case 'conferir': this.confirmarValores(); break;
            case 'uso':
                this.uso = v;
                this.depoisDoUso();
                break;
            case 'grau-ok': this.ir(this.multi ? 'rotina' : 'telas'); break;
            case 'rotina':
                this.resp.rotina = v;
                this.campo = campo(v);
                this.ir('telas');
                break;
            case 'telas':
                this.resp.telas = v;
                this.ir('sol');
                break;
            case 'sol':
                this.resp.sol = v;
                this.calcular();
                break;
            case 'aplicar': this.aplicar(); break;
            case 'configurador': this.abrirConfigurador(); break;
            case 'multifocal': this.aplicarMultifocal(); break;
            case 'ampliar': this.ampliar(); break;
            case 'zoom-fechar': {
                var z = this.modal.querySelector('.lah-rxl__zoom');
                if (z) z.remove();
                break;
            }
            case 'dicas': {
                var d = this.modal.querySelector('.lah-rxl__dicas');
                if (d) d.classList.toggle('is-open');
                el.setAttribute('aria-expanded', d && d.classList.contains('is-open') ? 'true' : 'false');
                break;
            }
        }
    };

    Leitor.prototype.mudanca = function (e) {
        var t = e.target;
        if (t.classList.contains('lah-rxl__arquivo')) {
            var f = t.files && t.files[0];
            if (f) this.enviarFoto(f);
            return;
        }
        if (t.hasAttribute('data-campo')) {
            this.lerForm();
            /* eixo so vale com cilindro: liga/desliga sem redesenhar a tela */
            var campo = t.getAttribute('data-campo');
            if (/\.cil$/.test(campo)) {
                var eixo = this.modal.querySelector('[data-campo="' + campo.replace('.cil', '.eixo') + '"]');
                if (eixo) {
                    var tem = !!num(t.value);
                    eixo.disabled = !tem;
                    eixo.parentNode.classList.toggle('is-off', !tem);
                }
            }
        }
    };

    /* Arrastar e soltar a receita na tela inicial (desktop). */
    Leitor.prototype.arrastar = function (e, dentro) {
        if (this.tela !== 'inicio' && this.tela !== 'erro') return;
        e.preventDefault();
        var zona = this.modal.querySelector('.lah-rxl__zona');
        if (zona) zona.classList.toggle('is-sobre', dentro);
    };

    Leitor.prototype.soltar = function (e) {
        if (this.tela !== 'inicio' && this.tela !== 'erro') return;
        e.preventDefault();
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && this.endpoint) this.enviarFoto(f);
    };

    Leitor.prototype.ampliar = function () {
        if (!this.preview) return;
        var z = document.createElement('div');
        z.className = 'lah-rxl__zoom';
        z.innerHTML = '<img src="' + esc(this.preview) + '" alt="Sua receita ampliada">' +
            '<button type="button" class="lah-rxl__icone lah-rxl__zoom-fechar" data-acao="zoom-fechar" aria-label="Fechar foto ampliada">' + IC.fechar + '</button>';
        this.modal.appendChild(z);
        z.querySelector('button').focus();
    };

    /* ---------------- foto ---------------- */

    function base64(blob) {
        return new Promise(function (res, rej) {
            var r = new FileReader();
            r.onload = function () { res(String(r.result).split(',')[1]); };
            r.onerror = rej;
            r.readAsDataURL(blob);
        });
    }

    /* Reduz a foto para ~2400px no lado maior: a IA le melhor um JPEG
       nitido de tamanho moderado do que uma foto de 12 MP, e o envio fica
       rapido no 4G. */
    function preparar(file) {
        var MAX = 2400;
        if (file.type === 'application/pdf') {
            if (file.size > 8 * 1024 * 1024) return Promise.reject(new Error('grande'));
            return base64(file).then(function (d) { return { tipo: 'application/pdf', dados: d }; });
        }
        return new Promise(function (res, rej) {
            var url = URL.createObjectURL(file);
            var img = new Image();
            img.onload = function () {
                var w = img.naturalWidth, h = img.naturalHeight;
                var k = Math.min(1, MAX / Math.max(w, h));
                var c = document.createElement('canvas');
                c.width = Math.round(w * k);
                c.height = Math.round(h * k);
                var ctx = c.getContext('2d');
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, c.width, c.height);
                ctx.drawImage(img, 0, 0, c.width, c.height);
                URL.revokeObjectURL(url);
                c.toBlob(function (b) {
                    if (!b) { rej(new Error('canvas')); return; }
                    base64(b).then(function (d) { res({ tipo: 'image/jpeg', dados: d }); }, rej);
                }, 'image/jpeg', 0.88);
            };
            img.onerror = function () {
                URL.revokeObjectURL(url);
                /* formato que o navegador nao decodifica: envia como veio */
                var ok = ['image/jpeg', 'image/png', 'image/webp'].indexOf(file.type) !== -1;
                if (ok && file.size <= 7 * 1024 * 1024) {
                    base64(file).then(function (d) { res({ tipo: file.type, dados: d }); }, rej);
                } else {
                    rej(new Error('formato'));
                }
            };
            img.src = url;
        });
    }

    /* Animacao da leitura: os passos avancam no tempo e o ultimo so fecha
       quando a resposta chega. Tempo minimo na tela para a leitura nao
       "piscar" quando a resposta vem rapido. */
    Leitor.prototype.animarLeitura = function () {
        var self = this;
        this.passo = 0;
        this.frase = 0;
        [1400, 3200, 5400].forEach(function (ms, i) {
            self.timers.push(setTimeout(function () {
                if (self.tela !== 'lendo') return;
                self.passo = i + 1;
                self.atualizarPassos();
            }, ms));
        });
        this.timers.push(setInterval(function () {
            if (self.tela !== 'lendo') return;
            self.frase = (self.frase + 1) % FRASES.length;
            var el = self.modal.querySelector('.lah-rxl__frase');
            if (!el) return;
            if (REDUZIR) { el.textContent = FRASES[self.frase]; return; }
            el.classList.add('is-saindo');
            self.timers.push(setTimeout(function () {
                el.textContent = FRASES[self.frase];
                el.classList.remove('is-saindo');
            }, 280));
        }, 3600));
    };

    Leitor.prototype.atualizarPassos = function () {
        var self = this;
        this.modal.querySelectorAll('.lah-rxl__passos li').forEach(function (li, i) {
            li.className = i < self.passo ? 'is-feito' : (i === self.passo ? 'is-atual' : '');
        });
    };

    Leitor.prototype.enviarFoto = function (file) {
        var self = this;
        if (file.size > 20 * 1024 * 1024) {
            this.erroLeitura = 'O arquivo passa de 20 MB. Tente uma foto menor.';
            this.ir('erro');
            return;
        }
        this.arquivo = file;
        this.origem = 'foto';
        if (this.preview) { try { URL.revokeObjectURL(this.preview); } catch (e) { } }
        this.preview = file.type.indexOf('image/') === 0 ? URL.createObjectURL(file) : '';
        this.ir('lendo');
        this.animarLeitura();

        var inicio = Date.now();
        var minimo = new Promise(function (res) { setTimeout(res, 2600); });
        var leitura;

        if (this.endpoint === 'demo') {
            leitura = new Promise(function (res) { setTimeout(function () { res(JSON.parse(JSON.stringify(DEMO))); }, 1400); });
        } else {
            leitura = preparar(file).then(function (p) {
                var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
                var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 45000);
                return fetch(self.endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tipo: p.tipo, dados: p.dados }),
                    signal: ctrl ? ctrl.signal : undefined
                }).then(function (r) {
                    clearTimeout(timer);
                    return r.json().catch(function () { return { ok: false }; }).then(function (j) {
                        if (!r.ok || !j || !j.ok) {
                            var err = new Error((j && j.erro) || 'servidor');
                            err.codigo = (j && j.codigo) || r.status;
                            throw err;
                        }
                        return j.leitura;
                    });
                }, function (err) { clearTimeout(timer); throw err; });
            });
        }

        Promise.all([leitura, minimo]).then(function (r) {
            var l = r[0];
            if (self.tela !== 'lendo') return; /* cliente fechou ou voltou */
            var seg = Math.round((Date.now() - inicio) / 1000);
            if (!l || !l.e_receita) {
                track('rx_leitor_foto', { status: 'nao_e_receita', segundos: seg });
                self.erroLeitura = 'Não encontramos uma receita de óculos nesta imagem. Envie uma foto que mostre a receita inteira.';
                self.ir('erro', true);
                return;
            }
            if (!l.legivel) {
                track('rx_leitor_foto', { status: 'ilegivel', segundos: seg });
                self.erroLeitura = 'A foto ficou difícil de ler. Tente outra com boa luz, sem sombra e com a receita inteira no quadro.';
                self.ir('erro', true);
                return;
            }
            self.leitura = l;
            self.preencherForm(l);
            track('rx_leitor_foto', { status: 'ok', confianca: l.confianca || '', segundos: seg });
            self.passo = PASSOS_LEITURA.length;
            self.atualizarPassos();
            self.timers.push(setTimeout(function () {
                if (self.tela === 'lendo') self.ir('conferir', true);
            }, REDUZIR ? 0 : 450));
        }).catch(function (err) {
            if (self.tela !== 'lendo') return;
            track('rx_leitor_foto', { status: 'erro', codigo: String((err && err.codigo) || (err && err.message) || '') });
            if (err && err.message === 'grande') {
                self.erroLeitura = 'O PDF passa de 8 MB. Envie uma foto da receita.';
            } else if (err && err.message === 'formato') {
                self.erroLeitura = 'Não conseguimos abrir este arquivo. Envie uma foto em JPG ou PNG, ou um PDF.';
            } else if (err && err.codigo === 429) {
                self.erroLeitura = 'Muitas leituras em pouco tempo. Aguarde um minuto e tente de novo, ou digite os valores.';
            } else {
                self.erroLeitura = 'A leitura não foi concluída. Tente de novo ou digite os valores da receita.';
            }
            self.ir('erro', true);
        });
    };

    /* Leitura -> formulario de conferencia, ja no formato do configurador. */
    Leitor.prototype.preencherForm = function (l) {
        var rx = montaReceita(l);
        function f(o) {
            if (o.vazio) return { esf: '', cil: '', eixo: '' };
            return { esf: valorSelect(o.esf), cil: valorSelect(o.cil), eixo: o.eixo === null ? '' : String(o.eixo) };
        }
        this.form = {
            modo: rx.modo,
            od: f(rx.od),
            oe: f(rx.oe),
            add: rx.add ? String(rx.add) : ''
        };
        this.transposto = rx.od.transposto || rx.oe.transposto;
        this.prisma = rx.prisma;
    };

    Leitor.prototype.lerForm = function () {
        var self = this;
        if (!this.modal) return;
        this.modal.querySelectorAll('[data-campo]').forEach(function (s) {
            var c = s.getAttribute('data-campo').split('.');
            if (c.length === 2) self.form[c[0]][c[1]] = s.value;
            else self.form[c[0]] = s.value;
        });
    };

    Leitor.prototype.confirmarValores = function () {
        this.lerForm();
        var f = this.form, erros = {};
        ['od', 'oe'].forEach(function (k) {
            var cil = num(f[k].cil);
            if (cil && !eixoNum(f[k].eixo)) erros[k] = 'Informe o eixo. Ele fica ao lado do cilindro (CIL) na receita.';
        });
        var vazio = [f.od.esf, f.od.cil, f.oe.esf, f.oe.cil].every(function (v) { return !num(v); });
        if (vazio && !(f.modo === 'longe' && num(f.add))) erros.geral = 'Preencha os valores da receita.';
        this.erros = erros;
        if (Object.keys(erros).length) { this.render(); return; }

        var leitura = f.modo === 'perto'
            ? { longe: {}, perto: { od: f.od, oe: f.oe }, prisma: this.prisma }
            : { longe: { od: f.od, oe: f.oe }, adicao: f.add, prisma: this.prisma };
        this.rx = montaReceita(leitura);
        this.uso = '';

        if (this.rx.modo === 'longe' && this.rx.add) { this.ir('uso'); return; }
        this.uso = this.rx.modo === 'perto' ? 'perto' : 'longe';
        this.depoisDoUso();
    };

    /* O configurador deste produto tem o caminho multifocal completo?
       (campo de visao + espessura multifocal + tratamento) */
    Leitor.prototype.temMulti = function () {
        var w = this.wrap;
        return !!(itemEl(w, STEP_TIPO, 'multifocal') && itemEl(w, STEP_CAMPO, 'base') && itemEl(w, STEP_ESP_MULTI, 'multifocal-normal'));
    };

    Leitor.prototype.depoisDoUso = function () {
        this.multi = false;
        this.campo = null;
        if (this.uso === 'multifocal') {
            /* sem o caminho completo no configurador: so leva ate a multifocal */
            if (!this.temMulti()) { this.ir('multifocal'); return; }
            this.multi = true;
            this.par = { od: this.rx.od, oe: this.rx.oe };
            var perto = paraUso(this.rx, 'perto');
            this.fora = foraDaFaixa(this.par, this.rx.prisma) || foraDaFaixa(perto, false);
            if (this.fora) {
                track('rx_leitor_resultado', { origem: this.origem, resultado: 'fora_da_faixa', motivo: this.fora, tipo: 'multifocal' });
                this.ir('fora');
                return;
            }
            this.esp = espessuraMulti(this.par, this.rx.add);
            if (!this.esp) { this.fora = 'grau'; this.ir('fora'); return; }
            this.ir('grau');
            return;
        }
        var par = paraUso(this.rx, this.uso);
        this.par = par;
        this.fora = foraDaFaixa(par, this.rx.prisma);
        if (this.fora) {
            track('rx_leitor_resultado', { origem: this.origem, resultado: 'fora_da_faixa', motivo: this.fora });
            this.ir('fora');
            return;
        }
        if (semGrau(par.od) && semGrau(par.oe)) { this.ir('semgrau'); return; }
        this.esp = espessura(par, this.cfg);
        if (!this.esp) { this.fora = 'grau'; this.ir('fora'); return; }
        this.ir('grau');
    };

    Leitor.prototype.calcular = function () {
        this.trat = tratamento(this.resp);
        track('rx_leitor_resultado', {
            origem: this.origem,
            resultado: 'indicacao',
            uso: this.uso,
            tipo: this.multi ? 'multifocal' : 'monofocal',
            campo: this.campo ? this.campo.handle : '',
            espessura: this.esp.handle,
            tratamento: this.trat.handle,
            confianca: this.leitura ? (this.leitura.confianca || '') : 'manual'
        });
        this.ir('resumo');
    };

    /* ---------------- aplicar no configurador ---------------- */

    Leitor.prototype.abrirLens = function () {
        if (!this.wrap) return false;
        var btn = this.wrap.querySelector('.openProductLens');
        if (!btn) return false;
        global.__lahLensSource = 'leitor_receita';
        btn.click();
        return true;
    };

    Leitor.prototype.clicar = function (step, handle) {
        var el = itemEl(this.wrap, step, handle);
        if (!el) return false;
        el.click();
        return true;
    };

    Leitor.prototype.aplicar = function () {
        var self = this;
        this.fechar(true);
        if (!this.abrirLens()) return;
        var ok = this.multi
            ? this.clicar(STEP_TIPO, 'multifocal')
                && this.clicar(STEP_CAMPO, this.campo.handle)
                && this.clicar(STEP_ESP_MULTI, this.esp.handle)
                && this.clicar(STEP_TRAT, this.trat.handle)
            : this.clicar(STEP_TIPO, 'monofocal')
                && this.clicar(STEP_ESP, this.esp.handle)
                && this.clicar(STEP_TRAT, this.trat.handle);
        track('rx_leitor_aplicar', {
            origem: this.origem,
            tipo: this.multi ? 'multifocal' : 'monofocal',
            campo: this.campo ? this.campo.handle : '',
            espessura: this.esp.handle,
            tratamento: this.trat.handle,
            completo: ok
        });
        if (ok) setTimeout(function () { self.prefillReceita(); }, 0);
    };

    Leitor.prototype.aplicarMultifocal = function () {
        this.fechar(true);
        if (!this.abrirLens()) return;
        var ok = this.clicar(STEP_TIPO, 'multifocal');
        track('rx_leitor_aplicar', { origem: this.origem, uso: 'multifocal', completo: ok });
    };

    Leitor.prototype.abrirConfigurador = function () {
        track('rx_leitor_configurador', { tela: this.tela });
        this.fechar(true);
        this.abrirLens();
    };

    /* Cliente de recompra tem a etapa "Sua receita" no configurador. Se ele
       caiu nela, a foto ja vai anexada (ou os valores ja vao digitados) —
       ele so confere e segue. Cliente novo nao tem a etapa: a receita segue
       sendo enviada pela area "Minhas Receitas", como hoje. */
    Leitor.prototype.prefillReceita = function () {
        try {
            if (!global.Alpine || !this.wrap) return;
            var d = global.Alpine.$data(this.wrap);
            if (!d || !d.prescriptionEnabled || d.currentStep !== d.prescriptionStep) return;
            var arq = this.arquivo;
            var aceito = arq && ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].indexOf(arq.type) !== -1 && arq.size <= 20 * 1024 * 1024;
            if (this.origem === 'foto' && aceito) {
                d.selectRxMethod('upload');
                d.rxFile = arq;
                d.rxFileName = arq.name;
                return;
            }
            var sinal = typeof global.lahRxFormatSigned === 'function' ? global.lahRxFormatSigned : function (v) { return String(v); };
            /* valores da lente que sera feita: para perto, ja com a adicao
               somada; na multifocal, o grau de longe mais a adicao */
            var par = this.par;
            if (!par) return;
            d.selectRxMethod('manual');
            d.rx.odEsf = sinal(par.od.esf); d.rx.odCil = sinal(par.od.cil); d.rx.odEixo = par.od.eixo ? String(par.od.eixo) : '';
            d.rx.oeEsf = sinal(par.oe.esf); d.rx.oeCil = sinal(par.oe.cil); d.rx.oeEixo = par.oe.eixo ? String(par.oe.eixo) : '';
            if (this.multi && this.rx && this.rx.add) d.rx.add = sinal(this.rx.add);
        } catch (e) { /* o configurador segue funcionando sem o preenchimento */ }
    };

    /* ---------------- moldura ---------------- */

    Leitor.prototype.render = function (novaTela) {
        var t = this.tela;
        var p = this['tela_' + t] ? this['tela_' + t]() : { corpo: '' };
        var etapa = ETAPA_DA_TELA[t] || 0;
        var podeVoltar = this.hist.length > 0 && t !== 'lendo';

        var progresso = '<ol class="lah-rxl__etapas" aria-label="Etapas">' + ETAPAS.map(function (nome, i) {
            var c = i < etapa ? 'is-feita' : (i === etapa ? 'is-atual' : '');
            return '<li class="' + c + '"' + (i === etapa ? ' aria-current="step"' : '') + '><span>' + nome + '</span></li>';
        }).join('') + '</ol>';

        var topo =
            (podeVoltar ? '<button type="button" class="lah-rxl__icone" data-acao="voltar" aria-label="Voltar">' + IC.voltar + '</button>' : '<span class="lah-rxl__icone lah-rxl__icone--vazio"></span>') +
            progresso +
            '<button type="button" class="lah-rxl__icone" data-acao="fechar" aria-label="Fechar">' + IC.fechar + '</button>';

        var caixa = this.modal.querySelector('.lah-rxl__caixa');
        if (!caixa) {
            this.modal.innerHTML =
                '<div class="lah-rxl__caixa">' +
                '<header class="lah-rxl__topo"></header>' +
                '<div class="lah-rxl__grade">' +
                '<aside class="lah-rxl__visual"></aside>' +
                '<section class="lah-rxl__conteudo"><div class="lah-rxl__corpo"></div><footer class="lah-rxl__rodape"></footer></section>' +
                '</div>' +
                '<input type="file" class="lah-rxl__arquivo" accept="image/*,application/pdf" hidden>' +
                '</div>';
            caixa = this.modal.querySelector('.lah-rxl__caixa');
        }
        var grade = caixa.querySelector('.lah-rxl__grade');
        grade.setAttribute('data-tela', t);
        grade.setAttribute('data-visual', p.tipoVisual || 'nenhum');
        caixa.querySelector('.lah-rxl__topo').innerHTML = topo;
        caixa.querySelector('.lah-rxl__visual').innerHTML = p.visual || '';
        var corpo = caixa.querySelector('.lah-rxl__corpo');
        corpo.innerHTML = '<div class="lah-rxl__tela">' + (p.corpo || '') + '</div>';
        var rod = caixa.querySelector('.lah-rxl__rodape');
        rod.innerHTML = p.rodape || '';
        rod.hidden = !p.rodape;

        if (novaTela) {
            corpo.scrollTop = 0;
            var h = corpo.querySelector('h2');
            if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
        }
    };

    /* Visuais da coluna esquerda */
    Leitor.prototype.visualFoto = function () {
        if (!this.preview) return { tipoVisual: 'intro', visual: '<div class="lah-rxl__painel">' + ILUSTRACAO + '</div>' };
        return {
            tipoVisual: 'foto',
            visual: '<figure class="lah-rxl__foto">' +
                '<img src="' + esc(this.preview) + '" alt="Foto da sua receita">' +
                '<button type="button" class="lah-rxl__ampliar" data-acao="ampliar">' + IC.ampliar + '<span>Ampliar</span></button>' +
                '</figure>'
        };
    };

    Leitor.prototype.visualArmacao = function (legenda) {
        var a = this.arm || {};
        if (!a.imagem) return this.visualFoto();
        return {
            tipoVisual: 'armacao',
            visual: '<figure class="lah-rxl__armacao">' +
                '<img src="' + esc(a.imagem) + '" alt="' + esc(a.titulo + ' ' + a.variante) + '">' +
                '<figcaption>' + (legenda || ('Para o seu ' + esc(a.titulo) + (a.variante ? ' ' + esc(a.variante) : ''))) + '</figcaption>' +
                '</figure>'
        };
    };

    function comVisual(v, partes) {
        partes.visual = v.visual;
        partes.tipoVisual = v.tipoVisual;
        return partes;
    }

    /* ---------------- telas ---------------- */

    Leitor.prototype.tela_inicio = function () {
        var foto = this.endpoint;
        var corpo =
            '<h2 class="lah-rxl__titulo" style="margin-bottom:24px">Envie sua receita. A gente indica a lente.</h2>' +
            '<ol class="lah-rxl__roteiro">' +
            '<li><strong>' + (foto ? 'Envie a foto da receita' : 'Digite os valores da receita') + '</strong><span>' + (foto ? 'A inteligência artificial lê os valores de cada olho.' : 'Copie os números com o sinal + ou −.') + '</span></li>' +
            '<li><strong>Confira o que foi lido</strong><span>Você ajusta qualquer valor antes de seguir.</span></li>' +
            '<li><strong>Responda perguntas rápidas</strong><span>Sobre o seu dia a dia, para indicar a lente certa.</span></li>' +
            '</ol>' +
            (foto
                ? '<button type="button" class="lah-rxl__zona" data-acao="foto">' +
                '<span class="lah-rxl__zona-icone">' + IC.foto + '</span>' +
                '<span class="lah-rxl__zona-texto"><strong>Enviar foto da receita</strong>' +
                '<small class="so-toque">Tire uma foto agora ou escolha da galeria</small>' +
                '<small class="so-mouse">Escolha o arquivo ou arraste para cá. JPG, PNG ou PDF.</small></span>' +
                '</button>' +
                '<button type="button" class="lah-rxl__dicas-botao" data-acao="dicas" aria-expanded="false">Como tirar uma boa foto</button>' +
                '<ul class="lah-rxl__dicas">' +
                '<li>A receita inteira no quadro, com as linhas de OD e OE visíveis.</li>' +
                '<li>Luz natural, sem sombra e sem flash.</li>' +
                '<li>Celular paralelo ao papel, sem inclinar.</li>' +
                '</ul>'
                : '');
        var rodape =
            (foto ? '' : '<button type="button" class="button button--primary button--full" data-acao="digitar">Digitar os valores</button>') +
            (foto ? '<button type="button" class="lah-rxl__link-sec" data-acao="digitar">' + IC.lapis + '<span>Prefiro digitar os valores</span></button>' : '') +
            (foto ? '<p class="lah-rxl__privacidade">' + IC.cadeado + '<span>A foto é usada só para esta leitura e não fica guardada.</span></p>' : '');
        return {
            tipoVisual: 'intro',
            visual: '<div class="lah-rxl__painel">' + ILUSTRACAO +
                '<p class="lah-rxl__painel-texto">Leitor de receita com inteligência artificial</p></div>',
            corpo: corpo,
            rodape: rodape
        };
    };

    Leitor.prototype.tela_lendo = function () {
        var self = this;
        var lente = '<div class="lah-rxl__lente' + (this.preview ? '' : ' is-sem-foto') + '">' +
            (this.preview ? '<img src="' + esc(this.preview) + '" alt="">' : IC.lente) +
            '<span class="lah-rxl__varredura" aria-hidden="true"></span>' +
            '</div>';
        var passos = PASSOS_LEITURA.map(function (p, i) {
            var c = i < self.passo ? 'is-feito' : (i === self.passo ? 'is-atual' : '');
            return '<li class="' + c + '"><i aria-hidden="true">' + IC.check + '</i><span>' + p + '</span></li>';
        }).join('');
        return {
            tipoVisual: 'lente',
            visual: '<div class="lah-rxl__painel lah-rxl__painel--lente">' + lente + '<p class="lah-rxl__painel-texto">Leitura com inteligência artificial</p></div>',
            corpo:
                '<div class="lah-rxl__lente-mobile">' + lente + '</div>' +
                '<h2 class="lah-rxl__titulo">Lendo sua receita</h2>' +
                '<ol class="lah-rxl__passos" aria-live="polite">' + passos + '</ol>' +
                '<div class="lah-rxl__saiba">' +
                '<span>Enquanto isso</span>' +
                '<p class="lah-rxl__frase">' + esc(FRASES[this.frase % FRASES.length]) + '</p>' +
                '</div>'
        };
    };

    Leitor.prototype.tela_erro = function () {
        return comVisual(this.visualFoto(), {
            corpo:
                '<h2 class="lah-rxl__titulo">Não foi possível ler a receita</h2>' +
                '<p class="lah-rxl__texto">' + esc(this.erroLeitura) + '</p>' +
                (this.endpoint ? '<ul class="lah-rxl__dicas is-open"><li>A receita inteira no quadro, com as linhas de OD e OE visíveis.</li><li>Luz natural, sem sombra e sem flash.</li><li>Celular paralelo ao papel, sem inclinar.</li></ul>' : ''),
            rodape:
                (this.endpoint ? '<button type="button" class="button button--primary button--full" data-acao="foto">Enviar outra foto</button>' : '') +
                '<button type="button" class="lah-rxl__link-sec" data-acao="digitar">' + IC.lapis + '<span>Digitar os valores</span></button>'
        });
    };

    Leitor.prototype.linhaOlho = function (k, rotulo) {
        var o = this.form[k];
        var temCil = !!num(o.cil);
        return '<div class="lah-rxl__olho" role="group" aria-label="' + rotulo + '">' +
            '<span class="lah-rxl__olho-nome">' + rotulo + '</span>' +
            '<label class="lah-rxl__campo"><span>ESF</span><select data-campo="' + k + '.esf">' + opcoes(ESF_OPC, o.esf === '' ? 0 : o.esf) + '</select></label>' +
            '<label class="lah-rxl__campo"><span>CIL</span><select data-campo="' + k + '.cil">' + opcoes(CIL_OPC, o.cil === '' ? 0 : o.cil) + '</select></label>' +
            '<label class="lah-rxl__campo' + (temCil ? '' : ' is-off') + '"><span>Eixo</span><select data-campo="' + k + '.eixo"' + (temCil ? '' : ' disabled') + '>' + opcoesEixo(o.eixo) + '</select></label>' +
            (this.erros[k] ? '<p class="lah-rxl__erro">' + esc(this.erros[k]) + '</p>' : '') +
            '</div>';
    };

    Leitor.prototype.tela_conferir = function () {
        var f = this.form;
        var lida = this.origem === 'foto' && this.leitura;
        var avisos = '';
        /* Informativo (nao alerta): so aparece quando a leitura nao saiu
           totalmente precisa ou a IA deixou uma observacao. */
        var obs = lida ? String(this.leitura.observacao || '').trim() : '';
        var imprecisa = lida && this.leitura.confianca && this.leitura.confianca !== 'alta';
        if (imprecisa || obs) {
            avisos += '<div class="lah-rxl__info" role="note">' + IC.info +
                '<p>Antes de seguir, confira os valores com a sua receita original.</p>' +
                '</div>';
        }
        if (lida && this.prisma) {
            avisos += '<p class="lah-rxl__alerta">A receita indica prisma. Esse ajuste é feito com atendimento: indicamos o caminho no final.</p>';
        }
        var notas = '';
        if (lida && this.transposto) notas += '<p class="lah-rxl__nota">Sua receita usa cilindro positivo. Convertemos para o formato do laboratório (cilindro negativo): é a mesma lente.</p>';

        var v = lida ? this.visualFoto() : this.visualArmacao();
        return comVisual(v, {
            corpo:
                (lida && this.preview ? '<button type="button" class="lah-rxl__miniatura" data-acao="ampliar"><img src="' + esc(this.preview) + '" alt=""><span>' + IC.ampliar + 'Ver a foto da receita</span></button>' : '') +
                '<h2 class="lah-rxl__titulo">' + (lida ? 'Confira o que lemos' : 'Valores da sua receita') + '</h2>' +
                '<p class="lah-rxl__texto">' + (lida ? 'Compare com a sua receita e ajuste se algo estiver diferente.' : 'Copie os números da linha Longe, com o sinal + ou −. Se a receita tiver Adição (ADD), informe também.') + '</p>' +
                avisos +
                /* Sem escolha de longe/perto: quase toda receita e para longe.
                   Perto so entra quando a foto traz grau apenas no campo Perto
                   (a leitura identifica sozinha) ou quando ha adicao (a
                   pergunta de uso aparece depois). */
                (f.modo === 'perto' ? '<div class="lah-rxl__info" role="note">' + IC.info + '<p>Sua receita tem grau só no campo Perto: vamos indicar a lente para leitura.</p></div>' : '') +
                '<div class="lah-rxl__tabela">' +
                this.linhaOlho('od', 'Olho direito (OD)') +
                this.linhaOlho('oe', 'Olho esquerdo (OE)') +
                (f.modo === 'longe'
                    ? '<div class="lah-rxl__olho lah-rxl__olho--add" role="group" aria-label="Adição"><span class="lah-rxl__olho-nome">Adição, se houver</span>' +
                    '<label class="lah-rxl__campo"><span>ADD</span><select data-campo="add">' + opcoes(ADD_OPC, f.add, 'Não tem') + '</select></label></div>'
                    : '') +
                '</div>' +
                (this.erros.geral ? '<p class="lah-rxl__erro">' + esc(this.erros.geral) + '</p>' : '') +
                notas,
            rodape: '<button type="button" class="button button--primary button--full" data-acao="conferir">' + (lida ? 'Os valores conferem' : 'Continuar') + '</button>'
        });
    };

    Leitor.prototype.tela_uso = function () {
        return comVisual(this.visualFoto(), {
            corpo:
                '<h2 class="lah-rxl__titulo">Como você vai usar estes óculos?</h2>' +
                '<p class="lah-rxl__texto">Sua receita tem adição (' + esc(fmt(this.rx.add)) + '), o grau extra para ver de perto.</p>' +
                '<div class="lah-rxl__opcoes">' +
                '<button type="button" class="lah-rxl__opcao" data-acao="uso" data-valor="multifocal"><span class="lah-rxl__opcao-texto"><strong>Longe e perto nos mesmos óculos</strong><small>Lente multifocal</small></span></button>' +
                '<button type="button" class="lah-rxl__opcao" data-acao="uso" data-valor="perto"><span class="lah-rxl__opcao-texto"><strong>Só para perto</strong><small>Leitura, celular e computador próximo</small></span></button>' +
                '<button type="button" class="lah-rxl__opcao" data-acao="uso" data-valor="longe"><span class="lah-rxl__opcao-texto"><strong>Só para longe</strong><small>Dirigir, TV e uso no dia a dia</small></span></button>' +
                '</div>'
        });
    };

    Leitor.prototype.cartaoOlho = function (o, nome) {
        if (semGrau(o)) {
            return '<div class="lah-rxl__olho-res"><div class="lah-rxl__olho-res-topo"><strong>' + nome + '</strong><span>Sem grau</span></div></div>';
        }
        var valores = '<span><small>ESF</small>' + esc(fmt(o.esf)) + '</span>';
        if (o.cil) valores += '<span><small>CIL</small>' + esc(fmt(o.cil)) + '</span><span><small>Eixo</small>' + (o.eixo ? o.eixo + '°' : '—') + '</span>';
        return '<div class="lah-rxl__olho-res">' +
            '<div class="lah-rxl__olho-res-topo"><strong>' + nome + '</strong><span class="lah-rxl__valores">' + valores + '</span></div>' +
            classifica(o).map(medidor).join('') +
            '</div>';
    };

    Leitor.prototype.tela_grau = function () {
        var p = this.par;
        var perto = this.uso === 'perto';
        var multi = this.multi;
        var tit = titulo(p.od, p.oe);
        if (multi && tit === 'Sem grau') tit = 'Vista cansada (presbiopia)';
        var cartaoAdd = multi
            ? '<div class="lah-rxl__olho-res">' +
            '<div class="lah-rxl__olho-res-topo"><strong>Perto (adição)</strong><span class="lah-rxl__valores"><span><small>ADD</small>' + esc(fmt(this.rx.add)) + '</span></span></div>' +
            '<p class="lah-rxl__olho-res-texto">Grau extra para leitura e celular. Na multifocal, ele fica na parte de baixo da lente.</p>' +
            '</div>'
            : '';
        return comVisual(this.visualFoto(), {
            corpo:
                '<p class="lah-rxl__sobre">' + (multi ? 'Seu grau para longe e perto' : (perto ? 'Seu grau para perto' : 'Seu grau')) + '</p>' +
                '<h2 class="lah-rxl__titulo">' + esc(tit) + '</h2>' +
                this.cartaoOlho(p.od, 'Olho direito') +
                this.cartaoOlho(p.oe, 'Olho esquerdo') +
                cartaoAdd +
                (perto && this.rx.modo === 'longe' ? '<p class="lah-rxl__nota">Para perto, somamos a adição ao grau de longe. É assim que o laboratório faz a lente de leitura.</p>' : '') +
                '<p class="lah-rxl__nota">As faixas leve, moderada e alta são referências gerais para explicar a receita. Não substituem a avaliação do seu oftalmologista.</p>',
            rodape: '<button type="button" class="button button--primary button--full" data-acao="grau-ok">Continuar</button>'
        });
    };

    Leitor.prototype.tela_rotina = function () {
        function op(v, titulo, sub) {
            return '<button type="button" class="lah-rxl__opcao" data-acao="rotina" data-valor="' + v + '"><span class="lah-rxl__opcao-texto"><strong>' + titulo + '</strong><small>' + sub + '</small></span></button>';
        }
        return comVisual(this.visualArmacao(), {
            corpo:
                '<div class="lah-rxl__pergunta-icone">' + IC.rotina + '</div>' +
                '<h2 class="lah-rxl__titulo">Como é o seu dia com estes óculos?</h2>' +
                '<p class="lah-rxl__texto">Isso define o campo de visão da multifocal: o tamanho da área nítida para cada distância.</p>' +
                '<div class="lah-rxl__opcoes">' +
                op('parado', 'Mais tempo parado', 'Leitura, celular e TV') +
                op('alterna', 'Alterno o dia todo', 'Perto, computador e longe, várias vezes por dia') +
                op('movimento', 'Muito em movimento', 'Dirijo, caminho, rotina intensa fora de casa') +
                '</div>'
        });
    };

    Leitor.prototype.tela_telas = function () {
        return comVisual(this.visualArmacao(), {
            corpo:
                '<div class="lah-rxl__pergunta-icone">' + IC.tela + '</div>' +
                '<h2 class="lah-rxl__titulo">Quanto tempo por dia você passa em telas?</h2>' +
                '<p class="lah-rxl__texto">Celular, computador, tablet e TV somados.</p>' +
                '<div class="lah-rxl__opcoes">' +
                '<button type="button" class="lah-rxl__opcao" data-acao="telas" data-valor="pouco"><span class="lah-rxl__opcao-texto"><strong>Menos de 2 horas</strong></span></button>' +
                '<button type="button" class="lah-rxl__opcao" data-acao="telas" data-valor="medio"><span class="lah-rxl__opcao-texto"><strong>De 2 a 6 horas</strong></span></button>' +
                '<button type="button" class="lah-rxl__opcao" data-acao="telas" data-valor="muito"><span class="lah-rxl__opcao-texto"><strong>Mais de 6 horas</strong></span></button>' +
                '</div>'
        });
    };

    Leitor.prototype.tela_sol = function () {
        return comVisual(this.visualArmacao(), {
            corpo:
                '<div class="lah-rxl__pergunta-icone">' + IC.sol + '</div>' +
                '<h2 class="lah-rxl__titulo">Quer que a lente escureça no sol?</h2>' +
                '<p class="lah-rxl__texto">A lente fotossensível escurece ao ar livre e volta a ficar clara em ambientes internos.</p>' +
                '<div class="lah-rxl__opcoes lah-rxl__opcoes--duas">' +
                '<button type="button" class="lah-rxl__opcao" data-acao="sol" data-valor="sim"><span class="lah-rxl__opcao-texto"><strong>Sim</strong></span></button>' +
                '<button type="button" class="lah-rxl__opcao" data-acao="sol" data-valor="nao"><span class="lah-rxl__opcao-texto"><strong>Não</strong></span></button>' +
                '</div>'
        });
    };

    Leitor.prototype.tela_resumo = function () {
        var a = this.arm || {};
        var multi = this.multi;
        var esp = this.esp, trat = this.trat;

        /* Perfil da lente: compara com a Normal no grau do cliente */
        var par = this.par;
        var olhoForte = forte(par.od) >= forte(par.oe) ? par.od : par.oe;
        var positivo = (olhoForte.esf + olhoForte.cil / 2) > 0;
        var linhaEsp = esp.fator === 1 ? 'Espessura padrão' : (esp.pct ? esp.pct + ' mais fina que a Normal' : 'A mais fina e leve das opções');

        var itens = [];
        if (multi) {
            itens.push({ rotulo: 'Campo de visão', nome: this.campo.nome, linha: this.campo.linha, preco: precoItem(this.wrap, STEP_CAMPO, this.campo.handle) });
        }
        itens.push({
            rotulo: 'Espessura', nome: esp.nome, linha: linhaEsp,
            preco: precoItem(this.wrap, multi ? STEP_ESP_MULTI : STEP_ESP, esp.handle),
            extra: esp.fator < 1 ? '<div class="lah-rxl__item-perfil">' + perfilLente(esp.maior, positivo, esp.fator, esp.nome) + '</div>' : ''
        });
        itens.push({ rotulo: 'Tratamento', nome: trat.nome, linha: trat.linha, preco: precoItem(this.wrap, STEP_TRAT, trat.handle) });

        var semPreco = itens.some(function (i) { return i.preco === null; });
        var total = (a.preco || 0) + itens.reduce(function (s, i) { return s + (i.preco || 0); }, 0);

        var lista = '<ul class="lah-rxl__itens" aria-label="Sua lente">' + itens.map(function (i) {
            var preco = i.preco === null ? '' : '<span class="lah-rxl__preco">' + (i.preco > 0 ? '+ ' + dinheiro(i.preco) : 'Incluso') + '</span>';
            return '<li class="lah-rxl__item">' +
                '<span class="lah-rxl__item-rotulo">' + i.rotulo + '</span>' +
                '<span class="lah-rxl__item-nome"><strong>' + esc(i.nome) + '</strong><small>' + esc(i.linha) + '</small></span>' +
                preco +
                (i.extra || '') +
                '</li>';
        }).join('') + '</ul>';

        /* Balao: o motivo de cada escolha, a partir das respostas */
        var porques = [];
        if (!multi && this.uso === 'perto' && this.rx.modo === 'longe' && this.rx.add) {
            porques.push('Como é para perto, somamos a adição de ' + fmt(this.rx.add) + ' ao seu grau de longe.');
        }
        if (multi) porques.push(this.campo.porque);
        porques.push(esp.porque);
        porques.push(trat.porque);
        var balao = '<div class="lah-rxl__balao" role="note">' +
            '<p class="lah-rxl__balao-titulo">' + IC.dica + '<span>Por que esta indicação</span></p>' +
            '<ul>' + porques.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>' +
            '</div>';

        var inclusos = ['Antirreflexo', 'Antirrisco', 'Proteção UV400'];
        if (!multi) inclusos.push('Pronta em 1 dia útil');

        var titulo = multi ? 'Multifocal ' + this.campo.nome : esp.nome + ' + ' + trat.nome;
        var sub = multi ? 'Longe e perto nos mesmos óculos' : (this.uso === 'perto' ? 'Lente com grau para perto' : 'Lente com grau para longe');
        var nomeArm = esc(a.titulo) + (a.variante ? ' ' + esc(a.variante) : '');

        var corpo =
            (a.imagem ? '<div class="lah-rxl__armacao-mobile"><img src="' + esc(a.imagem) + '" alt=""><span>Para o seu ' + nomeArm + '</span></div>' : '') +
            '<p class="lah-rxl__sobre">Sua lente ideal</p>' +
            '<h2 class="lah-rxl__titulo lah-rxl__titulo--grande">' + esc(titulo) + '</h2>' +
            '<p class="lah-rxl__subtitulo">' + sub + '</p>' +
            lista +
            balao +
            '<div class="lah-rxl__inclusos"><span>Incluso em todas</span><ul>' +
            inclusos.map(function (t) { return '<li>' + IC.check + t + '</li>'; }).join('') +
            '</ul></div>' +
            (trat.fotossensivel ? '<p class="lah-rxl__nota">Dentro do carro, a lente fotossensível escurece pouco: o para-brisa bloqueia boa parte do UV que ativa a lente.</p>' : '') +
            '<p class="lah-rxl__nota">Indicação feita a partir dos valores da sua receita. Na dúvida, fale com a gente pelo WhatsApp ou confirme com seu oftalmologista.</p>';

        var rodape =
            (total > 0 && !semPreco
                ? '<div class="lah-rxl__total"><span>Armação + lentes</span><strong>' + dinheiro(total) + '</strong><small>ou 6x de ' + dinheiro(Math.round(total / 6)) + ' sem juros</small></div>'
                : '') +
            '<button type="button" class="button button--primary button--full" data-acao="aplicar">Aplicar aos meus óculos</button>' +
            '<button type="button" class="lah-rxl__link-sec" data-acao="configurador"><span>Prefiro escolher as opções</span></button>';

        return comVisual(this.visualArmacao('Seu ' + nomeArm + '<br><span>' + esc(multi ? titulo : 'Lente ' + titulo) + '</span>'), {
            corpo: corpo,
            rodape: rodape
        });
    };

    Leitor.prototype.linkWhats = function (msg) {
        if (!this.whats) return '';
        return '<a class="button button--primary button--full lah-rxl__whats" target="_blank" rel="noopener" href="https://wa.me/' + esc(this.whats) + '?text=' + encodeURIComponent(msg) + '">' + IC.whats + '<span>Falar no WhatsApp</span></a>';
    };

    Leitor.prototype.tela_fora = function () {
        var prisma = this.fora === 'prisma';
        return comVisual(this.visualFoto(), {
            corpo:
                '<h2 class="lah-rxl__titulo">' + (prisma ? 'Sua receita tem prisma' : 'Seu grau pede um atendimento') + '</h2>' +
                '<p class="lah-rxl__texto">' + (prisma
                    ? 'Lentes com prisma são feitas sob medida. Fale com a gente para confirmar os detalhes antes da compra.'
                    : 'Seu grau está fora das faixas das lentes do site (esférico até ' + fmt(-10) + ' ou ' + fmt(10) + ', cilindro até ' + fmt(-6) + '). Fale com a gente: verificamos com o laboratório.') + '</p>',
            rodape: this.linkWhats('Olá! Usei o leitor de receita do site e preciso de ajuda para escolher a lente.') +
                '<button type="button" class="lah-rxl__link-sec" data-acao="configurador"><span>Ver as opções de lente</span></button>'
        });
    };

    Leitor.prototype.tela_semgrau = function () {
        return comVisual(this.visualFoto(), {
            corpo:
                '<h2 class="lah-rxl__titulo">Não encontramos grau para este uso</h2>' +
                '<p class="lah-rxl__texto">Os valores informados estão zerados. Confira a receita. Se você quer um Lahtref sem grau, as opções sem grau estão no configurador.</p>',
            rodape: '<button type="button" class="button button--primary button--full" data-acao="voltar">Revisar valores</button>' +
                '<button type="button" class="lah-rxl__link-sec" data-acao="configurador"><span>Ver as opções de lente</span></button>'
        });
    };

    Leitor.prototype.tela_multifocal = function () {
        var tem = !!itemEl(this.wrap, STEP_TIPO, 'multifocal');
        return comVisual(this.visualArmacao(), {
            corpo:
                '<p class="lah-rxl__sobre">Sua lente ideal</p>' +
                '<h2 class="lah-rxl__titulo lah-rxl__titulo--grande">Multifocal</h2>' +
                '<p class="lah-rxl__texto">Para enxergar de longe e de perto nos mesmos óculos, a lente indicada é a multifocal. No próximo passo você escolhe o campo de visão.</p>',
            rodape: tem ? '<button type="button" class="button button--primary button--full" data-acao="multifocal">Seguir para a multifocal</button>'
                : this.linkWhats('Olá! Usei o leitor de receita do site e quero uma lente multifocal.')
        });
    };

    /* Delegacao no documento: funciona tambem para atalhos inseridos depois
       do carregamento (gaveta de compra rapida, secoes recarregadas no editor). */
    function leitorDe(raiz) {
        if (!raiz.__lahRxl) raiz.__lahRxl = new Leitor(raiz);
        return raiz.__lahRxl;
    }

    if (!global.__lahRxlPronto) {
        global.__lahRxlPronto = true;
        document.addEventListener('click', function (e) {
            var btn = e.target.closest && e.target.closest('.lah-rxl-link');
            if (!btn) return;
            var raiz = btn.closest('[data-lah-rxl]');
            if (!raiz) return;
            e.preventDefault();
            leitorDe(raiz).abrir();
        });
    }

    global.LahRxLeitor = { abrir: function (raiz) { leitorDe(raiz || document.querySelector('[data-lah-rxl]')).abrir(); } };

})(typeof window !== 'undefined' ? window : globalThis);
